import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { siteConfig } from '../config/siteConfig';

const LOGO = "https://customer-assets.emergentagent.com/job_e8d411ed-442e-4bb2-8983-8c3ef5c754a7/artifacts/0cxz66vb_IMG_20260403_184615.jpg";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Track active dropdowns on mobile
  const [mobileLearnOpen, setMobileLearnOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'shadow-lg bg-white/97 backdrop-blur-md' : 'bg-white shadow-sm'
      }`}
    >
      {/* Top bar */}
      <div className="bg-[#C8161D] text-white text-xs py-1.5 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <span className="font-poppins">Welcome to {siteConfig.name} — {siteConfig.tagline}</span>
          <a href={`tel:${siteConfig.phoneRaw}`} className="flex items-center gap-1.5 hover:text-red-200 transition-colors font-poppins">
            <Phone size={11} /> {siteConfig.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <Link to="/" data-testid="header-logo">
            <img src={LOGO} alt={siteConfig.name} className="h-14 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-5">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `text-sm font-semibold font-poppins transition-colors duration-200 relative group pb-1 ${
                  isActive ? 'text-[#C8161D]' : 'text-[#1A1A1A] hover:text-[#C8161D]'
                }`
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/about"
              className={({ isActive }) =>
                `text-sm font-semibold font-poppins transition-colors duration-200 relative group pb-1 ${
                  isActive ? 'text-[#C8161D]' : 'text-[#1A1A1A] hover:text-[#C8161D]'
                }`
              }
            >
              About Us
            </NavLink>

            {/* Learn Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-semibold font-poppins text-[#1A1A1A] hover:text-[#C8161D] transition-colors pb-1">
                Learn <ChevronDown size={14} />
              </button>
              <div className="absolute top-full -left-4 hidden group-hover:block w-48 bg-white border border-gray-100 rounded-2xl shadow-xl py-2 mt-0 z-50">
                <Link to="/programs" className="block px-5 py-2.5 text-sm font-poppins text-[#1A1A1A] hover:bg-[#FDE8E9] hover:text-[#C8161D] transition-colors">
                  Our Programs
                </Link>
                <Link to="/academics" className="block px-5 py-2.5 text-sm font-poppins text-[#1A1A1A] hover:bg-[#FDE8E9] hover:text-[#C8161D] transition-colors">
                  Academics
                </Link>
                <Link to="/activities" className="block px-5 py-2.5 text-sm font-poppins text-[#1A1A1A] hover:bg-[#FDE8E9] hover:text-[#C8161D] transition-colors">
                  Activities
                </Link>
              </div>
            </div>

            <NavLink
              to="/admissions"
              className={({ isActive }) =>
                `text-sm font-semibold font-poppins transition-colors duration-200 relative group pb-1 ${
                  isActive ? 'text-[#C8161D]' : 'text-[#1A1A1A] hover:text-[#C8161D]'
                }`
              }
            >
              Admissions
            </NavLink>

            {/* Explore Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm font-semibold font-poppins text-[#1A1A1A] hover:text-[#C8161D] transition-colors pb-1">
                Explore <ChevronDown size={14} />
              </button>
              <div className="absolute top-full -left-4 hidden group-hover:block w-48 bg-white border border-gray-100 rounded-2xl shadow-xl py-2 mt-0 z-50">
                <Link to="/facilities" className="block px-5 py-2.5 text-sm font-poppins text-[#1A1A1A] hover:bg-[#FDE8E9] hover:text-[#C8161D] transition-colors">
                  Facilities
                </Link>
                <Link to="/gallery" className="block px-5 py-2.5 text-sm font-poppins text-[#1A1A1A] hover:bg-[#FDE8E9] hover:text-[#C8161D] transition-colors">
                  Gallery
                </Link>
                <Link to="/parents-corner" className="block px-5 py-2.5 text-sm font-poppins text-[#1A1A1A] hover:bg-[#FDE8E9] hover:text-[#C8161D] transition-colors">
                  Parents Corner
                </Link>
              </div>
            </div>

            <NavLink
              to="/blog"
              className={({ isActive }) =>
                `text-sm font-semibold font-poppins transition-colors duration-200 relative group pb-1 ${
                  isActive ? 'text-[#C8161D]' : 'text-[#1A1A1A] hover:text-[#C8161D]'
                }`
              }
            >
              Blog
            </NavLink>

            <NavLink
              to="/careers"
              className={({ isActive }) =>
                `text-sm font-semibold font-poppins transition-colors duration-200 relative group pb-1 ${
                  isActive ? 'text-[#C8161D]' : 'text-[#1A1A1A] hover:text-[#C8161D]'
                }`
              }
            >
              Careers
            </NavLink>

            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `text-sm font-semibold font-poppins transition-colors duration-200 relative group pb-1 ${
                  isActive ? 'text-[#C8161D]' : 'text-[#1A1A1A] hover:text-[#C8161D]'
                }`
              }
            >
              Contact Us
            </NavLink>

            <Link to="/admissions" data-testid="enroll-now-btn" className="btn-primary !text-xs !px-4 !py-2.5 ml-1">
              Enroll Now
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            {open ? <X size={24} className="text-[#1A1A1A]" /> : <Menu size={24} className="text-[#1A1A1A]" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-y-auto transition-all duration-300 ${open ? 'max-h-[85vh] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
        <div className="bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-1.5 shadow-inner">
          <NavLink
            to="/"
            end
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `py-2.5 px-4 rounded-xl font-medium text-base transition-all ${
                isActive ? 'bg-[#FDE8E9] text-[#C8161D] font-semibold' : 'text-[#1A1A1A]'
              }`
            }
          >
            Home
          </NavLink>

          <NavLink
            to="/about"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `py-2.5 px-4 rounded-xl font-medium text-base transition-all ${
                isActive ? 'bg-[#FDE8E9] text-[#C8161D] font-semibold' : 'text-[#1A1A1A]'
              }`
            }
          >
            About Us
          </NavLink>

          {/* Expandable Learn section on mobile */}
          <div>
            <button
              onClick={() => setMobileLearnOpen(!mobileLearnOpen)}
              className="w-full flex items-center justify-between py-2.5 px-4 rounded-xl font-medium text-base text-[#1A1A1A]"
            >
              <span>Learn</span>
              <ChevronDown size={16} className={`transition-transform duration-200 ${mobileLearnOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`pl-6 flex flex-col gap-1 overflow-hidden transition-all duration-200 ${mobileLearnOpen ? 'max-h-40 py-1' : 'max-h-0'}`}>
              <Link to="/programs" onClick={() => setOpen(false)} className="py-2 px-4 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50">
                Our Programs
              </Link>
              <Link to="/academics" onClick={() => setOpen(false)} className="py-2 px-4 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50">
                Academics
              </Link>
              <Link to="/activities" onClick={() => setOpen(false)} className="py-2 px-4 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50">
                Activities
              </Link>
            </div>
          </div>

          <NavLink
            to="/admissions"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `py-2.5 px-4 rounded-xl font-medium text-base transition-all ${
                isActive ? 'bg-[#FDE8E9] text-[#C8161D] font-semibold' : 'text-[#1A1A1A]'
              }`
            }
          >
            Admissions
          </NavLink>

          {/* Expandable Explore section on mobile */}
          <div>
            <button
              onClick={() => setMobileExploreOpen(!mobileExploreOpen)}
              className="w-full flex items-center justify-between py-2.5 px-4 rounded-xl font-medium text-base text-[#1A1A1A]"
            >
              <span>Explore</span>
              <ChevronDown size={16} className={`transition-transform duration-200 ${mobileExploreOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`pl-6 flex flex-col gap-1 overflow-hidden transition-all duration-200 ${mobileExploreOpen ? 'max-h-40 py-1' : 'max-h-0'}`}>
              <Link to="/facilities" onClick={() => setOpen(false)} className="py-2 px-4 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50">
                Facilities
              </Link>
              <Link to="/gallery" onClick={() => setOpen(false)} className="py-2 px-4 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50">
                Gallery
              </Link>
              <Link to="/parents-corner" onClick={() => setOpen(false)} className="py-2 px-4 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50">
                Parents Corner
              </Link>
            </div>
          </div>

          <NavLink
            to="/blog"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `py-2.5 px-4 rounded-xl font-medium text-base transition-all ${
                isActive ? 'bg-[#FDE8E9] text-[#C8161D] font-semibold' : 'text-[#1A1A1A]'
              }`
            }
          >
            Blog
          </NavLink>

          <NavLink
            to="/careers"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `py-2.5 px-4 rounded-xl font-medium text-base transition-all ${
                isActive ? 'bg-[#FDE8E9] text-[#C8161D] font-semibold' : 'text-[#1A1A1A]'
              }`
            }
          >
            Careers
          </NavLink>

          <NavLink
            to="/contact"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `py-2.5 px-4 rounded-xl font-medium text-base transition-all ${
                isActive ? 'bg-[#FDE8E9] text-[#C8161D] font-semibold' : 'text-[#1A1A1A]'
              }`
            }
          >
            Contact Us
          </NavLink>

          <Link
            to="/admissions"
            onClick={() => setOpen(false)}
            className="btn-primary text-center mt-2 justify-center"
          >
            Enroll Now
          </Link>
        </div>
      </div>
    </header>
  );
}
